import React, {
    useEffect,
    useState
} from "react";

import {
    Link
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";

import QuillEditor from "../components/QuillEditor";


function AddQuestion() {

    /*
    |--------------------------------------------------------------------------
    | API URLs
    |--------------------------------------------------------------------------
    */

    const CATEGORY_API_URL =
        `${API_BASE_URL}/admin/question-paper-categories`;

    const PAPERS_API_URL =
        `${API_BASE_URL}/admin/question-papers`;

    const QUESTIONS_API_URL =
        `${API_BASE_URL}/admin/questions`;


    /*
    |--------------------------------------------------------------------------
    | Categories
    |--------------------------------------------------------------------------
    */

    const [categories, setCategories] =
        useState([]);


    /*
    |--------------------------------------------------------------------------
    | Papers
    |--------------------------------------------------------------------------
    */

    const [papers, setPapers] =
        useState([]);


    /*
    |--------------------------------------------------------------------------
    | Selected Category
    |--------------------------------------------------------------------------
    */

    const [selectedCategory, setSelectedCategory] =
        useState("");


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    const [loadingCategories, setLoadingCategories] =
        useState(true);

    const [loadingPapers, setLoadingPapers] =
        useState(false);

    const [saving, setSaving] =
        useState(false);


    /*
    |--------------------------------------------------------------------------
    | Error
    |--------------------------------------------------------------------------
    */

    const [error, setError] =
        useState("");


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({

        paper: [],

        question: "",

        sort_order: "",

        option1: "",

        option2: "",

        option3: "",

        option4: "",

        right_option: "",

        explanation: ""

    });


    /*
    |--------------------------------------------------------------------------
    | Fetch Categories
    |--------------------------------------------------------------------------
    */

    const fetchCategories = async () => {

        try {

            setLoadingCategories(true);

            setError("");


            const response =
                await fetch(
                    CATEGORY_API_URL,
                    {
                        method: "GET",

                        headers: {
                            Accept:
                                "application/json"
                        },

                        credentials:
                            "include"
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 401
            ) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    `Server Error: ${response.status}`
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Response
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            console.log(
                "Paper Category API Response:",
                data
            );


            if (data.success) {

                setCategories(
                    Array.isArray(data.data)
                        ? data.data
                        : []
                );

            } else {

                throw new Error(
                    data.message ||
                    "Unable to fetch paper categories."
                );

            }

        } catch (error) {

            console.error(
                "Paper Category API Error:",
                error
            );


            setError(
                error.message ||
                "Unable to connect with server."
            );

        } finally {

            setLoadingCategories(
                false
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Load Categories
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchCategories();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Fetch Papers By Category
    |--------------------------------------------------------------------------
    */

    const fetchPapersByCategory =
        async (category) => {

            /*
            |--------------------------------------------------------------------------
            | No Category
            |--------------------------------------------------------------------------
            */

            if (!category) {

                setPapers([]);

                setFormData(
                    (previous) => ({

                        ...previous,

                        paper: []

                    })
                );

                return;

            }


            try {

                setLoadingPapers(true);

                setError("");

                setPapers([]);


                /*
                |--------------------------------------------------------------------------
                | Reset Only Paper Selection
                |--------------------------------------------------------------------------
                |
                | Other fields remain unchanged.
                |
                */

                setFormData(
                    (previous) => ({

                        ...previous,

                        paper: []

                    })
                );


                /*
                |--------------------------------------------------------------------------
                | API Request
                |--------------------------------------------------------------------------
                */

                const response =
                    await fetch(
                        `${PAPERS_API_URL}/${encodeURIComponent(category)}`,
                        {
                            method: "GET",

                            headers: {
                                Accept:
                                    "application/json"
                            },

                            credentials:
                                "include"
                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | Unauthorized
                |--------------------------------------------------------------------------
                */

                if (
                    response.status === 401
                ) {

                    throw new Error(
                        "Admin authentication failed. Please login again."
                    );

                }


                /*
                |--------------------------------------------------------------------------
                | Server Error
                |--------------------------------------------------------------------------
                */

                if (!response.ok) {

                    throw new Error(
                        `Server Error: ${response.status}`
                    );

                }


                /*
                |--------------------------------------------------------------------------
                | Response
                |--------------------------------------------------------------------------
                */

                const data =
                    await response.json();


                console.log(
                    "Papers By Category API Response:",
                    data
                );


                if (data.success) {

                    setPapers(
                        Array.isArray(
                            data.papers
                        )
                            ? data.papers
                            : []
                    );

                } else {

                    throw new Error(
                        data.message ||
                        "Unable to fetch papers."
                    );

                }

            } catch (error) {

                console.error(
                    "Papers By Category API Error:",
                    error
                );


                setError(
                    error.message ||
                    "Unable to connect with server."
                );

            } finally {

                setLoadingPapers(
                    false
                );

            }

        };


    /*
    |--------------------------------------------------------------------------
    | Category Change
    |--------------------------------------------------------------------------
    */

    const handleCategoryChange =
        (event) => {

            const category =
                event.target.value;


            setSelectedCategory(
                category
            );


            fetchPapersByCategory(
                category
            );

        };


    /*
    |--------------------------------------------------------------------------
    | Normal Input Change
    |--------------------------------------------------------------------------
    */

    const handleChange =
        (event) => {

            const {
                name,
                value
            } = event.target;


            setFormData(
                (previous) => ({

                    ...previous,

                    [name]:
                        value

                })
            );

        };


    /*
    |--------------------------------------------------------------------------
    | Paper Change
    |--------------------------------------------------------------------------
    */

    const handlePaperChange =
        (event) => {

            const selectedOptions =
                Array.from(
                    event.target.selectedOptions
                );


            const selectedPaperIds =
                selectedOptions.map(
                    (option) =>
                        option.value
                );


            setFormData(
                (previous) => ({

                    ...previous,

                    paper:
                        selectedPaperIds

                })
            );

        };


    /*
    |--------------------------------------------------------------------------
    | Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit =
        async (event) => {

            event.preventDefault();


            /*
            |--------------------------------------------------------------------------
            | Category Validation
            |--------------------------------------------------------------------------
            */

            if (!selectedCategory) {

                Swal.fire(
                    "Validation Error",
                    "Please select paper category.",
                    "warning"
                );

                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Paper Validation
            |--------------------------------------------------------------------------
            */

            if (
                !Array.isArray(
                    formData.paper
                ) ||
                formData.paper.length === 0
            ) {

                Swal.fire(
                    "Validation Error",
                    "Please select at least one paper.",
                    "warning"
                );

                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Question Value
            |--------------------------------------------------------------------------
            */

            const questionValue =
                String(
                    formData.question || ""
                );


            /*
            |--------------------------------------------------------------------------
            | Question Text Validation
            |--------------------------------------------------------------------------
            */

            const questionText =
                questionValue
                    .replace(
                        /<[^>]*>/g,
                        ""
                    )
                    .replace(
                        /&nbsp;/g,
                        " "
                    )
                    .trim();


            if (!questionText) {

                Swal.fire(
                    "Validation Error",
                    "Please enter question.",
                    "warning"
                );

                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Options
            |--------------------------------------------------------------------------
            |
            | String() is used so undefined/null can never cause
            | .trim() runtime error.
            |
            */

            const option1 =
                String(
                    formData.option1 || ""
                ).trim();

            const option2 =
                String(
                    formData.option2 || ""
                ).trim();

            const option3 =
                String(
                    formData.option3 || ""
                ).trim();

            const option4 =
                String(
                    formData.option4 || ""
                ).trim();


            /*
            |--------------------------------------------------------------------------
            | Options Validation
            |--------------------------------------------------------------------------
            */

            if (
                !option1 ||
                !option2 ||
                !option3 ||
                !option4
            ) {

                Swal.fire(
                    "Validation Error",
                    "Please enter all four options.",
                    "warning"
                );

                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Right Option
            |--------------------------------------------------------------------------
            */

            const rightOption =
                String(
                    formData.right_option || ""
                ).trim();


            /*
            |--------------------------------------------------------------------------
            | Right Option Validation
            |--------------------------------------------------------------------------
            */

            if (!rightOption) {

                Swal.fire(
                    "Validation Error",
                    "Please select right option.",
                    "warning"
                );

                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Explanation
            |--------------------------------------------------------------------------
            |
            | Explanation is OPTIONAL.
            |
            | Empty explanation will be sent as NULL.
            |
            */

            const explanation =
                formData.explanation
                    ? String(
                        formData.explanation
                    )
                    : null;


            /*
            |--------------------------------------------------------------------------
            | Save
            |--------------------------------------------------------------------------
            */

            try {

                setSaving(true);

                setError("");


                /*
                |--------------------------------------------------------------------------
                | API Request
                |--------------------------------------------------------------------------
                */

                const response =
                    await fetch(
                        QUESTIONS_API_URL,
                        {
                            method: "POST",

                            headers: {

                                "Content-Type":
                                    "application/json",

                                Accept:
                                    "application/json"

                            },

                            credentials:
                                "include",

                            body:
                                JSON.stringify({

                                    paper:
                                        formData.paper,

                                    question:
                                        questionValue,

                                    sort_order:
                                        formData.sort_order || "",

                                    option1:
                                        option1,

                                    option2:
                                        option2,

                                    option3:
                                        option3,

                                    option4:
                                        option4,

                                    right_option:
                                        rightOption,

                                    explanation:
                                        explanation

                                })

                        }
                    );


                /*
                |--------------------------------------------------------------------------
                | Unauthorized
                |--------------------------------------------------------------------------
                */

                if (
                    response.status === 401
                ) {

                    throw new Error(
                        "Admin authentication failed. Please login again."
                    );

                }


                /*
                |--------------------------------------------------------------------------
                | Read Response
                |--------------------------------------------------------------------------
                */

                const data =
                    await response.json();


                console.log(
                    "Add Question Response:",
                    data
                );


                /*
                |--------------------------------------------------------------------------
                | Server Error
                |--------------------------------------------------------------------------
                */

                if (!response.ok) {

                    /*
                    |--------------------------------------------------------------------------
                    | Laravel Validation Errors
                    |--------------------------------------------------------------------------
                    */

                    if (
                        data.errors &&
                        typeof data.errors === "object"
                    ) {

                        const validationMessages =
                            Object.values(
                                data.errors
                            )
                                .flat()
                                .join("\n");


                        throw new Error(
                            validationMessages ||
                            data.message ||
                            `Server Error: ${response.status}`
                        );

                    }


                    throw new Error(
                        data.message ||
                        `Server Error: ${response.status}`
                    );

                }


                /*
                |--------------------------------------------------------------------------
                | Success
                |--------------------------------------------------------------------------
                */

                if (data.success) {

                    await Swal.fire({

                        icon:
                            "success",

                        title:
                            "Question Added",

                        text:
                            data.message ||
                            "Question added successfully.",

                        confirmButtonText:
                            "OK"

                    });


                    /*
                    |--------------------------------------------------------------------------
                    | Reset Only Question Fields
                    |--------------------------------------------------------------------------
                    |
                    | IMPORTANT:
                    |
                    | option1  -> NOT RESET
                    | option2  -> NOT RESET
                    | option3  -> NOT RESET
                    | option4  -> NOT RESET
                    | right_option -> NOT RESET
                    | paper -> NOT RESET
                    |
                    */

                    setFormData(
                        (previous) => ({

                            ...previous,

                            /*
                            |--------------------------------------------------------------------------
                            | Reset
                            |--------------------------------------------------------------------------
                            */

                            question:
                                "",

                            sort_order:
                                "",

                            explanation:
                                "",


                            /*
                            |--------------------------------------------------------------------------
                            | Keep Options
                            |--------------------------------------------------------------------------
                            */

                            option1:
                                previous.option1,

                            option2:
                                previous.option2,

                            option3:
                                previous.option3,

                            option4:
                                previous.option4,

                            right_option:
                                previous.right_option

                        })
                    );


                    /*
                    |--------------------------------------------------------------------------
                    | Clear Error
                    |--------------------------------------------------------------------------
                    */

                    setError("");

                } else {

                    throw new Error(
                        data.message ||
                        "Unable to add question."
                    );

                }

            } catch (error) {

                console.error(
                    "Add Question Error:",
                    error
                );


                setError(
                    error.message ||
                    "Unable to connect with server."
                );


                Swal.fire(
                    "Error",
                    error.message ||
                    "Unable to add question.",
                    "error"
                );

            } finally {

                setSaving(false);

            }

        };


    /*
    |--------------------------------------------------------------------------
    | Loading Categories
    |--------------------------------------------------------------------------
    */

    if (loadingCategories) {

        return (

            <div
                className="
                    container-fluid
                    d-flex
                    justify-content-center
                    align-items-center
                "
                style={{
                    minHeight:
                        "400px"
                }}
            >

                <div
                    className="text-center"
                >

                    <div
                        className="
                            spinner-border
                            text-primary
                        "
                        role="status"
                    />

                    <div
                        className="
                            mt-3
                            text-muted
                        "
                    >

                        Loading paper categories...

                    </div>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | UI
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="container-fluid"
            style={{
                maxHeight:
                    "85vh",

                overflowY:
                    "auto"
            }}
        >

            {/* =========================================================
                HEADER
            ========================================================= */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3
                        className="mb-1"
                    >

                        Add Question

                    </h3>


                    <p
                        className="
                            text-muted
                            mb-0
                        "
                    >

                        Add a new question

                    </p>

                </div>


                <Link
                    to="/questions"
                    className="
                        btn
                        btn-outline-primary
                    "
                >

                    <i
                        className="
                            fa
                            fa-arrow-left
                            me-2
                        "
                    />

                    Back

                </Link>

            </div>


            {/* =========================================================
                ERROR
            ========================================================= */}

            {error && (

                <div
                    className="
                        alert
                        alert-danger
                    "
                >

                    {error}

                </div>

            )}


            {/* =========================================================
                CARD
            ========================================================= */}

            <div
                className="
                    card
                    shadow-sm
                "
            >

                <div
                    className="
                        card-header
                        bg-white
                    "
                >

                    <h5
                        className="mb-0"
                    >

                        Question Details

                    </h5>

                </div>


                <div
                    className="card-body"
                >

                    <form
                        onSubmit={
                            handleSubmit
                        }
                    >

                        {/* =================================================
                            CATEGORY
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Select Paper Category

                                <span
                                    className="
                                        text-danger
                                    "
                                >

                                    {" "}*

                                </span>

                            </label>


                            <select
                                className="form-select"
                                value={
                                    selectedCategory
                                }
                                onChange={
                                    handleCategoryChange
                                }
                            >

                                <option value="">

                                    ----- Select Paper Category -----

                                </option>


                                {categories.length > 0 ? (

                                    categories.map(
                                        (category) => (

                                            <option
                                                key={
                                                    category.id
                                                }
                                                value={
                                                    category.paper
                                                }
                                            >

                                                {
                                                    category.paper
                                                }

                                            </option>

                                        )
                                    )

                                ) : (

                                    <option
                                        disabled
                                    >

                                        No paper categories found

                                    </option>

                                )}

                            </select>

                        </div>


                        {/* =================================================
                            PAPERS
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Select Paper

                                <span
                                    className="
                                        text-danger
                                    "
                                >

                                    {" "}*

                                </span>

                            </label>


                            <select
                                name="paper"
                                className="form-select"
                                multiple
                                size="6"
                                value={
                                    formData.paper
                                }
                                onChange={
                                    handlePaperChange
                                }
                                disabled={
                                    !selectedCategory ||
                                    loadingPapers
                                }
                            >

                                {!selectedCategory ? (

                                    <option disabled>

                                        ----- Select Paper Category First -----

                                    </option>

                                ) : loadingPapers ? (

                                    <option disabled>

                                        Loading papers...

                                    </option>

                                ) : papers.length > 0 ? (

                                    papers.map(
                                        (paper) => (

                                            <option
                                                key={
                                                    paper.paper_id
                                                }
                                                value={
                                                    paper.paper_id
                                                }
                                            >

                                                {
                                                    paper.paper
                                                }

                                                {" - "}

                                                {
                                                    paper.paper_type
                                                }

                                                {" - "}

                                                {
                                                    paper.category
                                                }

                                            </option>

                                        )
                                    )

                                ) : (

                                    <option disabled>

                                        No papers found for this category.

                                    </option>

                                )}

                            </select>


                            <small
                                className="
                                    text-muted
                                "
                            >

                                Hold Ctrl and select multiple
                                papers if required.

                            </small>

                        </div>


                        {/* =================================================
                            QUESTION
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Question

                                <span
                                    className="
                                        text-danger
                                    "
                                >

                                    {" "}*

                                </span>

                            </label>


                            <QuillEditor

                                value={
                                    formData.question || ""
                                }

                                allowImage={
                                    false
                                }

                                onChange={
                                    (value) =>
                                        setFormData(
                                            (previous) => ({

                                                ...previous,

                                                question:
                                                    value || ""

                                            })
                                        )
                                }

                            />

                        </div>


                        {/* =================================================
                            SORT ORDER
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Sort Order

                            </label>


                            <input
                                type="number"
                                name="sort_order"
                                className="form-control"
                                placeholder="Enter sort order"
                                value={
                                    formData.sort_order || ""
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </div>


                        {/* =================================================
                            OPTIONS
                        ================================================= */}

                        <div
                            className="row"
                        >

                            {/* OPTION 1 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >

                                    Option 1

                                </label>


                                <input
                                    type="text"
                                    name="option1"
                                    className="
                                        form-control
                                    "
                                    placeholder="Enter option 1"
                                    maxLength="80"
                                    value={
                                        formData.option1 || ""
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            {/* OPTION 2 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >

                                    Option 2

                                </label>


                                <input
                                    type="text"
                                    name="option2"
                                    className="
                                        form-control
                                    "
                                    placeholder="Enter option 2"
                                    maxLength="80"
                                    value={
                                        formData.option2 || ""
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            {/* OPTION 3 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >

                                    Option 3

                                </label>


                                <input
                                    type="text"
                                    name="option3"
                                    className="
                                        form-control
                                    "
                                    placeholder="Enter option 3"
                                    maxLength="80"
                                    value={
                                        formData.option3 || ""
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            {/* OPTION 4 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >

                                    Option 4

                                </label>


                                <input
                                    type="text"
                                    name="option4"
                                    className="
                                        form-control
                                    "
                                    placeholder="Enter option 4"
                                    maxLength="80"
                                    value={
                                        formData.option4 || ""
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>

                        </div>


                        {/* =================================================
                            RIGHT OPTION
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Right Option

                                <span
                                    className="
                                        text-danger
                                    "
                                >

                                    {" "}*

                                </span>

                            </label>


                            <select
                                name="right_option"
                                value={
                                    formData.right_option || ""
                                }
                                onChange={
                                    handleChange
                                }
                                className="
                                    form-select
                                "
                            >

                                <option value="">

                                    Select Right Option

                                </option>

                                <option value="1">
                                    1
                                </option>

                                <option value="2">
                                    2
                                </option>

                                <option value="3">
                                    3
                                </option>

                                <option value="4">
                                    4
                                </option>

                            </select>

                        </div>


                        {/* =================================================
                            EXPLANATION
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Explanation

                                <span
                                    className="
                                        text-muted
                                        ms-2
                                    "
                                >

                                    (Optional)

                                </span>

                            </label>


                            <QuillEditor

                                value={
                                    formData.explanation || ""
                                }

                                allowImage={
                                    true
                                }

                                onChange={
                                    (value) =>
                                        setFormData(
                                            (previous) => ({

                                                ...previous,

                                                explanation:
                                                    value || ""

                                            })
                                        )
                                }

                            />

                        </div>


                        {/* =================================================
                            BUTTONS
                        ================================================= */}

                        <div
                            className="
                                d-flex
                                gap-2
                            "
                        >

                            <button
                                type="submit"
                                className="
                                    btn
                                    btn-primary
                                "
                                disabled={
                                    saving
                                }
                            >

                                {saving ? (

                                    <>

                                        <span
                                            className="
                                                spinner-border
                                                spinner-border-sm
                                                me-2
                                            "
                                        />

                                        Saving...

                                    </>

                                ) : (

                                    <>

                                        <i
                                            className="
                                                fa
                                                fa-save
                                                me-2
                                            "
                                        />

                                        Save Question

                                    </>

                                )}

                            </button>


                            <Link
                                to="/questions"
                                className="
                                    btn
                                    btn-secondary
                                "
                            >

                                Cancel

                            </Link>

                        </div>

                    </form>

                </div>

            </div>

        </div>

    );

}


export default AddQuestion;